import { Button } from 'antd';
import { ArrowLeft, ArrowRight } from 'assets/images';
import { BaseSelect } from 'components/UI/BaseSelect';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PAGE_SIZE } from 'util/config/constant';

export const BaseTablePagination = ({
  onPageChange,
  pagination,
  onLimitChange,
  showRowsPerPage,
  pageSizeOptions
}) => {
  const { t } = useTranslation();
  const defaultPageSize = PAGE_SIZE;
  const { stateProp, page, limit } = pagination;
  const { total, isLoading } = useSelector((state) => ({
    total: state[stateProp].get('list').total,
    isLoading: state[stateProp].get('list').isLoading
  }));

  let to = page * limit;
  let from = to - limit + 1;
  if (to > total) {
    to = total;
  }
  if (!to) {
    from = 0;
  }
  const disabledPrevButton = useMemo(
    () => isLoading || page === 1,
    [isLoading, page]
  );

  const disabledNextButton = useMemo(() => {
    if (!total) return true;

    return isLoading || Math.ceil(total / limit) === page;
  }, [isLoading, limit, page, total]);

  const handleChange = (perPages) => {
    onLimitChange(perPages);
  };

  return (
    <section className="base-table__pagination-wrapper">
      {showRowsPerPage && (
        <div className="pagination-wrapper__page-size-wrapper">
          <span className="pagination-wrapper__row-per-page">
            {t('pagination.rowsPerPage')}
          </span>
          <BaseSelect
            className="pagination-wrapper__page-size"
            defaultValue={defaultPageSize}
            useCaretIcon
            onChange={handleChange}>
            {pageSizeOptions.map((pageSize) => (
              <BaseSelect.Option value={pageSize} key={pageSize}>
                {pageSize}
              </BaseSelect.Option>
            ))}
          </BaseSelect>
        </div>
      )}
      <Button
        className="pagination-wrapper__button"
        icon={<ArrowLeft />}
        type="link"
        disabled={disabledPrevButton}
        onClick={() => onPageChange(page - 1)}
      />

      <span className="pagination-wrapper__text">
        {' '}
        {from}-{to} {t('pagination.of')} {total}
      </span>

      <Button
        className="pagination-wrapper__button"
        icon={<ArrowRight />}
        type="link"
        onClick={() => onPageChange(page + 1)}
        disabled={disabledNextButton}
        data-testid="next-page"
      />
    </section>
  );
};

BaseTablePagination.propTypes = {
  onPageChange: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    stateProp: PropTypes.string.isRequired,
    page: PropTypes.number.isRequired,
    limit: PropTypes.number.isRequired
  }).isRequired,
  onLimitChange: PropTypes.func.isRequired,
  showRowsPerPage: PropTypes.bool.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number).isRequired
};
